/**
 * Distribution: the links people use to join a project.
 * - The Prolific study link (Prolific fills in each participant's ID).
 * - Personal invite links for people recruited directly, each with its own code, a label so you
 *   know who it was for, and whether it has been used. Invites are stored at
 *   projects/{projectId}/invites/{code}; a participant who registers through one has that code as
 *   their registration ID (registration.prolific.pId).
 */
import {
  collection, deleteDoc, doc, onSnapshot, orderBy, query, serverTimestamp, setDoc, where,
} from 'firebase/firestore';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useAuthContext } from '../../auth/AuthContext';
import { useAlert } from '../../context/AlertContext';
import { db } from '../../firebase';
import participantStage, { STAGE_LABEL } from '../utils/participantStage';

const muted = { fontSize: 13, color: 'var(--jp-gray-600)', margin: '0 0 12px', lineHeight: 1.5 };
const th = {
  textAlign: 'left', fontWeight: 500, fontSize: 12, color: 'var(--jp-gray-600)',
  padding: '6px 10px', borderBottom: '1px solid var(--jp-gray-200)', whiteSpace: 'nowrap',
};
const td = { padding: '7px 10px', borderBottom: '1px solid var(--jp-gray-100)', fontSize: 13, verticalAlign: 'middle' };
const codeBox = {
  fontFamily: 'var(--jp-mono)', fontSize: 12, padding: '8px 10px', background: 'var(--jp-gray-50)',
  border: '1px solid var(--jp-gray-200)', borderRadius: 'var(--jp-radius-sm)', wordBreak: 'break-all', flex: '1 1 320px',
};

/** A registration code: 24 characters (the registration page needs at least 23), random. */
function newCode() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(21);
  window.crypto.getRandomValues(bytes);
  return 'INV' + Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

const registrationLink = (projectId, code) =>
  `${window.location.origin}/reg/prolificRegistration/${projectId}/pId/${code}`;

function CopyButton({ text, label = 'Copy' }) {
  const { addAlert } = useAlert();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      addAlert('success', 'Copied');
    } catch {
      addAlert('error', 'Could not copy; select the text and copy it instead.');
    }
  };
  return <button type="button" className="ja-btn ja-btn-outline" onClick={copy}>{label}</button>;
}
CopyButton.propTypes = { text: PropTypes.string.isRequired, label: PropTypes.string };

const STATUS = { unused: ['not used yet', 'ja-chip-inactive'], ...STAGE_LABEL };

export default function ProjectDistributionPanel({ projectId }) {
  const { addAlert } = useAlert();
  const { userJamasp } = useAuthContext();
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [invites, setInvites] = useState(null);
  const [byCode, setByCode] = useState({});

  const prolificLink = registrationLink(projectId, '{{%PROLIFIC_PID%}}');

  // Invites for this project, newest first, live.
  useEffect(() => onSnapshot(
    query(collection(db, 'projects', projectId, 'invites'), orderBy('createdAt', 'desc')),
    (snap) => setInvites(snap.docs.map((d) => ({ code: d.id, ...d.data() }))),
    (err) => {
      console.error('invites query failed', err);
      setInvites([]);
    },
  ), [projectId]);

  // Which invites have been used, live: participants of this project whose registration ID is a
  // code (Firestore 'in' takes at most 30 values, so one listener per 30 codes).
  const codesKey = (invites || []).map((i) => i.code).join(',');
  useEffect(() => {
    const codes = codesKey ? codesKey.split(',') : [];
    setByCode({});
    if (!codes.length) return undefined;
    const chunks = [];
    for (let i = 0; i < codes.length; i += 30) chunks.push(codes.slice(i, i + 30));
    const parts = chunks.map(() => ({}));
    const unsubs = chunks.map((c, k) => onSnapshot(
      query(collection(db, 'participants'), where('projectId', '==', projectId),
        where('registration.prolific.pId', 'in', c)),
      (snap) => {
        parts[k] = {};
        snap.docs.forEach((d) => {
          const p = d.data();
          parts[k][p.registration?.prolific?.pId] = { id: d.id, stage: participantStage(p) };
        });
        setByCode(Object.assign({}, ...parts));
      },
      (err) => console.error('participant lookup failed', err),
    ));
    return () => unsubs.forEach((u) => u());
  }, [codesKey, projectId]);

  const createInvite = async () => {
    setCreating(true);
    try {
      const code = newCode();
      await setDoc(doc(db, 'projects', projectId, 'invites', code), {
        label: label.trim() || null,
        createdAt: serverTimestamp(),
        createdBy: userJamasp?.uid || null,
      });
      setLabel('');
      try {
        await navigator.clipboard.writeText(registrationLink(projectId, code));
        addAlert('success', 'Invite link created and copied');
      } catch {
        addAlert('success', 'Invite link created');
      }
    } catch (err) {
      addAlert('error', `Could not create the invite: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const removeInvite = async (code) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove this invite from the list? The link itself keeps working.')) return;
    try {
      await deleteDoc(doc(db, 'projects', projectId, 'invites', code));
    } catch (err) {
      addAlert('error', `Could not remove it: ${err.message}`);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="ja-alert ja-alert-info" style={{ fontSize: 13, lineHeight: 1.5 }}>
        Until Google verifies Jamasp&apos;s Google Health access, at most 100 Google accounts can ever connect
        (all projects together; see Google Auth Platform → Audience in the Jamasp-gcp console), and people see a
        &ldquo;Google hasn&apos;t verified this app&rdquo; notice first. The registration page tells them to press Continue.
      </div>

      <div className="ja-card">
        <div className="ja-card-header">
          <h3 className="ja-card-title">Prolific study link</h3>
        </div>
        <p style={muted}>
          Use this as the study URL in Prolific. Prolific replaces <code>{'{{%PROLIFIC_PID%}}'}</code> with
          each participant&apos;s own ID, so every person registers separately.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={codeBox}>{prolificLink}</div>
          <CopyButton text={prolificLink} />
        </div>
      </div>

      <div className="ja-card">
        <div className="ja-card-header">
          <h3 className="ja-card-title">Personal invite links</h3>
        </div>
        <p style={muted}>
          For people you invite directly. Each link has its own code and works for one person; sending the
          same link to two people would register them as the same participant. The label is only for you.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
          <label style={{ fontSize: 13, flex: '1 1 240px' }}>Who is it for? (optional)<br />
            <input className="ja-input" value={label} maxLength={80} placeholder="e.g. Arman"
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createInvite(); }} />
          </label>
          <button type="button" className="ja-btn ja-btn-primary" disabled={creating} onClick={createInvite}>
            Create invite link
          </button>
        </div>

        {invites === null && <p style={muted}>Loading…</p>}
        {invites?.length === 0 && <p style={muted}>No invite links yet.</p>}
        {invites?.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>For</th><th style={th}>Created</th><th style={th}>Status</th>
                  <th style={th}>Link</th><th style={th} aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => {
                  const used = byCode[inv.code];
                  const [statusLabel, cls] = STATUS[used ? used.stage : 'unused'];
                  return (
                    <tr key={inv.code}>
                      <td style={td}>{inv.label || <span style={{ color: 'var(--jp-gray-400)' }}>no label</span>}</td>
                      <td style={td}>{inv.createdAt?.toDate ? inv.createdAt.toDate().toLocaleDateString() : '…'}</td>
                      <td style={td}>
                        <span className={`ja-chip ${cls}`}>{statusLabel}</span>
                        {used && (
                          <a href={`/admin/participants/${used.id}/project/${projectId}`} style={{ marginLeft: 8, fontSize: 12 }}>
                            View
                          </a>
                        )}
                      </td>
                      <td style={{ ...td, fontFamily: 'var(--jp-mono)', fontSize: 11 }}>{inv.code}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <CopyButton text={registrationLink(projectId, inv.code)} label="Copy link" />
                        {' '}
                        <button type="button" className="ja-btn ja-btn-ghost" onClick={() => removeInvite(inv.code)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

ProjectDistributionPanel.propTypes = {
  projectId: PropTypes.string.isRequired,
};
